/**
 * Management → Traders. Review access requests and manage live traders.
 *
 * The approval button is the only place in TickView that grants someone the
 * ability to place real orders, so the UI is deliberately explicit about what
 * happens: it provisions a terminal, binds it, grants the role, and produces an
 * email the operator still has to send by hand.
 *
 * Rendered inside ManagementDashboard's main area, so it inherits its styles.
 */
import { Fragment, useCallback, useEffect, useState } from "react";
import { api, ApiError } from "@/api/client";
import { useAppDispatch, useCan } from "@/state/hooks";
import { CAP } from "@/api/capabilities";
import { pushToast } from "@/state/slices/notificationsSlice";
import type { AdminTrader, AdminTradersResponse, EmailDraft } from "@/api/types";
import styles from "./ManagementDashboard.module.css";

const REFRESH_MS = 15000;

const STATUS_LABEL: Record<string, string> = {
  pending: "Pending review",
  provisioning: "Setting up…",
  ready: "Ready to connect",
  connected: "Trading",
  rejected: "Rejected",
  revoked: "Revoked",
  failed: "Setup failed",
};

export function TradersView() {
  const dispatch = useAppDispatch();
  const canManageAdmins = useCan(CAP.adminsManage);
  const [data, setData] = useState<AdminTradersResponse | null>(null);
  const [drafts, setDrafts] = useState<EmailDraft[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [t, d] = await Promise.all([
        api.adminTraders(),
        api.adminEmailDrafts(),
      ]);
      setData(t);
      setDrafts(d.drafts);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load traders");
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const act = async (
    sub: string,
    label: string,
    fn: () => Promise<unknown>,
  ) => {
    setBusy(sub);
    setError(null);
    try {
      await fn();
      dispatch(pushToast({ kind: "success", title: label }));
      await load();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : `${label} failed`;
      setError(msg);
      dispatch(pushToast({ kind: "error", title: msg }));
    } finally {
      setBusy(null);
    }
  };

  const approve = (t: AdminTrader) =>
    act(t.sub, `Approved ${t.email}`, () => api.adminTraderApprove(t.sub));

  const reject = (t: AdminTrader) => {
    const reason = window.prompt(
      `Reject ${t.email}?\n\nOptional reason (they will see this):`,
      "",
    );
    if (reason === null) return;
    void act(t.sub, `Rejected ${t.email}`, () =>
      api.adminTraderReject(t.sub, reason),
    );
  };

  const revoke = (t: AdminTrader) => {
    const reason = window.prompt(
      `Revoke trading access for ${t.email}?\n\n` +
        "Their terminal keeps its session and any open positions — the folder " +
        "is not deleted. Optional reason:",
      "",
    );
    if (reason === null) return;
    void act(t.sub, `Revoked ${t.email}`, () =>
      api.adminTraderRevoke(t.sub, reason),
    );
  };

  const closeTerminal = (t: AdminTrader) => {
    if (
      !window.confirm(
        `Close the MT5 terminal for ${t.email}?\n\n` +
          "They stay a trader and do not need to request access again — they " +
          "just log in again. Open positions keep running at the broker.",
      )
    )
      return;
    void act(t.sub, `Closed the terminal for ${t.email}`, () =>
      api.adminTraderCloseTerminal(t.sub),
    );
  };

  const reprovision = (t: AdminTrader) =>
    act(t.sub, `Rebuilt terminal for ${t.email}`, () =>
      api.adminTraderReprovision(t.sub),
    );

  const copyDraft = async (d: EmailDraft) => {
    try {
      await navigator.clipboard.writeText(
        `To: ${d.to}\nSubject: ${d.subject}\n\n${d.body}`,
      );
      setCopied(d.id);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      /* clipboard blocked; the text is on screen */
    }
  };

  const markSent = async (d: EmailDraft) => {
    try {
      await api.adminMarkDraftSent(d.id);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not mark sent");
    }
  };

  const cap = data?.capacity;

  return (
    <div className={styles.tradersWrap}>
      {error && <div className={styles.tradersError}>{error}</div>}

      {/* Capacity — the real ceiling is desktop windows, not disk. */}
      {cap && (
        <div className={styles.tradersStats}>
          <span>
            <strong>{data?.summary.pending ?? 0}</strong> pending
          </span>
          <span>
            <strong>{data?.summary.connected ?? 0}</strong> trading
          </span>
          <span>
            <strong>
              {cap.provisioned}/{cap.max}
            </strong>{" "}
            terminals
          </span>
          <span>
            <strong>{cap.warm_spares}</strong> spare ready
          </span>
        </div>
      )}

      {/* Emails the operator still has to send by hand. */}
      {drafts.length > 0 && (
        <section className={styles.draftBox}>
          <h4 className={styles.draftTitle}>
            {drafts.length} email{drafts.length > 1 ? "s" : ""} to send
          </h4>
          {drafts.map((d) => (
            <div key={d.id} className={styles.draft}>
              <div className={styles.draftHead}>
                <span>
                  <strong>{d.to}</strong> — {d.subject}
                </span>
                <span className={styles.draftBtns}>
                  <button onClick={() => void copyDraft(d)}>
                    {copied === d.id ? "Copied ✓" : "Copy"}
                  </button>
                  <button onClick={() => void markSent(d)}>Mark sent</button>
                </span>
              </div>
              <pre className={styles.draftBody}>{d.body}</pre>
            </div>
          ))}
        </section>
      )}

      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>User</th>
              <th>Status</th>
              <th>Account</th>
              <th>Worker</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(data?.traders ?? []).length === 0 && (
              <tr>
                <td colSpan={5} className={styles.tradersEmpty}>
                  No trading access requests yet.
                </td>
              </tr>
            )}
            {(data?.traders ?? []).map((t) => (
              // Long-form Fragment: a row here is TWO <tr>s (the row + its
              // expanded detail), and `<>` cannot take a key. Keying the
              // fragment gives React stable identity across the 15s poll, which
              // re-sorts the roster.
              <Fragment key={t.sub}>
                <tr>
                  <td>
                    <div className={styles.userCell}>
                      <span className={styles.userName}>{t.name || t.email}</span>
                      <span className={styles.userMail}>{t.email}</span>
                    </div>
                  </td>
                  <td>
                    <span className={styles.statusPill}>
                      {STATUS_LABEL[t.status] ?? t.status}
                    </span>
                    {t.status === "failed" && t.last_error && (
                      <div className={styles.failNote}>{t.last_error}</div>
                    )}
                  </td>
                  <td>
                    {t.mt5
                      ? `${t.mt5.login_masked} · ${t.mt5.server}`
                      : <span className={styles.dimCell}>—</span>}
                  </td>
                  <td>
                    {t.status === "ready" || t.status === "connected" ? (
                      <span className={t.worker_up ? styles.okDot : styles.badDot}>
                        {t.worker_up ? "up" : "down"}
                      </span>
                    ) : (
                      <span className={styles.dimCell}>—</span>
                    )}
                  </td>
                  <td className={styles.actionCell}>
                    {t.status === "pending" && (
                      <>
                        <button
                          className={styles.approveBtn}
                          disabled={busy === t.sub}
                          onClick={() => void approve(t)}
                        >
                          {busy === t.sub ? "Approving…" : "Approve"}
                        </button>
                        <button
                          className={styles.rejectBtn}
                          disabled={busy === t.sub}
                          onClick={() => reject(t)}
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {(t.status === "ready" || t.status === "connected") &&
                      (!t.is_admin || canManageAdmins) && (
                        <>
                          <button
                            className={styles.rejectBtn}
                            disabled={busy === t.sub || !t.worker_up}
                            title={
                              t.worker_up
                                ? "Close the MT5 terminal; they keep trading access and just log in again"
                                : "Their terminal is not running — nothing to close"
                            }
                            onClick={() => closeTerminal(t)}
                          >
                            Close terminal
                          </button>
                          <button
                            className={styles.rejectBtn}
                            disabled={busy === t.sub}
                            onClick={() => revoke(t)}
                          >
                            Revoke
                          </button>
                        </>
                      )}
                    {t.status === "failed" && (
                      <button
                        className={styles.approveBtn}
                        disabled={busy === t.sub}
                        onClick={() => void reprovision(t)}
                      >
                        Retry setup
                      </button>
                    )}
                    <button
                      className={styles.detailBtn}
                      onClick={() =>
                        setExpanded(expanded === t.sub ? null : t.sub)
                      }
                    >
                      {expanded === t.sub ? "Hide" : "Details"}
                    </button>
                  </td>
                </tr>
                {expanded === t.sub && (
                  <tr>
                    <td colSpan={5} className={styles.detailRow}>
                      {t.broker && (
                        <p>
                          <strong>Broker:</strong> {t.broker}
                        </p>
                      )}
                      {t.note && (
                        <p>
                          <strong>Their note:</strong> {t.note}
                        </p>
                      )}
                      {t.terminal && (
                        <p className={styles.mono}>{t.terminal}</p>
                      )}
                      <ul className={styles.audit}>
                        {t.audit.map((a, i) => (
                          <li key={i}>
                            <span className={styles.auditWhen}>
                              {new Date(a.at).toLocaleString()}
                            </span>{" "}
                            <strong>{a.event}</strong>
                            {a.actor ? ` by ${a.actor}` : ""}
                            {a.detail ? ` — ${a.detail}` : ""}
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
